import { Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AbstractService } from "../../../../services/abstract.service";
import { ShipmentRequestInterface } from "./interfaces/create-order.interface";
import { IAPIItemResult, IAPIListResult, IPaginationResult } from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { CombinedFilter } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { CompanyAttributes, DocumentClassAttributes, MaterialAttributes, OrderAttributes, SubRouteAttributes } from "@albatrosdeveloper/ave-models-package";

import RouteAttributes from "@albatrosdeveloper/ave-models-package/src/schemas/routing/route/route.entity";
import BusinessPartnerAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity";
import DistributionChainAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/distribution-chain/distribution-chain.entity";
import SalesOfficeAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity";
import { FetchSequenceMaterialsFieldsRequest } from "./interfaces/fetch-sequence-materials-fields-request.interface";
import CurrencyAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/masters/currency/currency.entity";
import PaymentConditionAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/masters/payment-condition/payment-condition.entity";
import { MaterialsUMSelectionInterface } from "./interfaces/materials-um-selection-params.interface";

const routingUrl = env.API_ROUTING_URL + 'api/';
const materialUrl = env.API_MATERIAL_URL + 'api/';
const businessPartnerUrl = env.API_BUSINESS_PARTNER + 'api/';
const orderUrl = env.API_AVE_ORDER_URL + 'api/';
const configurationUrl = env.API_CONFIGURATION_URL + 'api/';
const documentUrl = env.API_DOCUMENT_URL + 'api/';
const masterUrl = env.API_UNIT_MEASURE + 'api/';
const moduleName = 'shipments-preparation/';

@Injectable({
  providedIn: "root",
})
export class OrdersCreationService extends AbstractService<
  OrderAttributes,
  Partial<OrderAttributes>,
  Partial<OrderAttributes>
> {
  constructor(
    http: HttpClient,
  ) {
    super(orderUrl, moduleName, http);
  }

  /**
   * List
   * @param filter CombinedFilter of related entity - RoutesAttributes
   * @returns Data list
   */
  fetchRoutes(
    filter?: CombinedFilter<RouteAttributes>,
  ): Observable<IAPIListResult<RouteAttributes>> {
    let API_URL: string = routingUrl + 'route/filter';
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<RouteAttributes>>(API_URL);
  }

  /**
   * List
   * @param filter CombinedFilter of related entity - SubRoutesAttributes
   * @returns Data list
   */
  fetchSubRoutes(
    filter?: CombinedFilter<SubRouteAttributes>,
  ): Observable<IAPIListResult<SubRouteAttributes>> {
    let API_URL: string = routingUrl + 'sub-route/filter';
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<SubRouteAttributes>>(API_URL);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - BusinessPartnerAttributes
    * @returns Data list
  */
  fetchBusinessPartners(
    filter?: CombinedFilter<BusinessPartnerAttributes>,
  ): Observable<IAPIListResult<BusinessPartnerAttributes>> {
    let API_URL: string = businessPartnerUrl + 'business-partner/filter';
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<BusinessPartnerAttributes>>(API_URL);
  }

  /**
    * Item
    * @param id String for _id of related entity - SalesOfficeAttributes
    * @returns Data Item
  */
  fetchSalesOfficeById(
    id: string,
  ): Observable<IAPIItemResult<SalesOfficeAttributes>> {
    let API_URL: string = `${configurationUrl}sales-office/byId/${id}`;
    return this.http.get<IAPIItemResult<SalesOfficeAttributes>>(API_URL);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - SalesOfficeAttributes
    * @returns Data list
  */
  fetchSalesOffices(
    filter?: CombinedFilter<SalesOfficeAttributes>,
  ): Observable<IAPIListResult<SalesOfficeAttributes>> {
    let API_URL: string = `${configurationUrl}sales-office/filter`;
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<SalesOfficeAttributes>>(API_URL);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - DocumentClassAttributes
    * @returns Data list
  */
  fetchDocumentClasses(
    filter?: CombinedFilter<DocumentClassAttributes>,
  ): Observable<IAPIListResult<DocumentClassAttributes>> {
    let API_URL: string = `${documentUrl}document_class/filter`;
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<DocumentClassAttributes>>(API_URL);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - CurrencyAttributes
    * @returns Data list
  */
  fetchCurrencies(
    filter?: CombinedFilter<CurrencyAttributes>,
  ): Observable<IAPIListResult<CurrencyAttributes>> {
    let API_URL: string = `${masterUrl}currency/filter`;
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<CurrencyAttributes>>(API_URL);
  }

  /**
    * Pagination
    * @param filter CombinedFilter of related entity - MaterialAttributes
    * @returns Paginated data
  */
  fetchMaterialsWithCount(
    filter?: CombinedFilter<MaterialAttributes>,
    fieldsData?: FetchSequenceMaterialsFieldsRequest,
    materialsUMSelection?: MaterialsUMSelectionInterface[]
  ): Observable<IPaginationResult<MaterialAttributes>> {
    let API_URL: string = materialUrl + 'material/sequence-pricing';
    let sequencePricingReq: {
      query: CombinedFilter<MaterialAttributes>,
      fieldsData?: FetchSequenceMaterialsFieldsRequest,
      materialsUMSelection?: MaterialsUMSelectionInterface[]
    } = { query: filter };
    if (fieldsData) sequencePricingReq.fieldsData = fieldsData;
    if (materialsUMSelection && materialsUMSelection.length) sequencePricingReq.materialsUMSelection = materialsUMSelection;
    return this.http.post<IPaginationResult<MaterialAttributes>>(API_URL, sequencePricingReq);
  }

  /**
    * @param ordersRequest Orders request to validate and request
    * @returns TODO
  */
  validateOrderRequest(
    ordersRequest: ShipmentRequestInterface,
  ): Observable<any> {
    let API_URL: string = orderUrl + moduleName + 'validate-and-create';
    return this.http.post<any>(API_URL, ordersRequest);
  }

  /**
    * List
    * @param filter CombinedFilter of related entity - PaymentConditionAttributes
    * @returns Data list
  */
  fetchPaymentConditions(
    filter?: CombinedFilter<PaymentConditionAttributes>,
  ): Observable<IAPIListResult<PaymentConditionAttributes>> {
    let API_URL: string = `${masterUrl}payment_condition/filter`;
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<PaymentConditionAttributes>>(API_URL);
  }

  /**
    * Item
    * @param id String for _id of related entity - CompanyAttributes
    * @returns Data Item
  */
  fetchCompanyById(
    id: string,
  ): Observable<IAPIItemResult<CompanyAttributes>> {
    let API_URL: string = `${configurationUrl}company/byId/${id}`;
    return this.http.get<IAPIItemResult<CompanyAttributes>>(API_URL);
  }

}
